import { useEffect, useState } from 'react';
import { venueCoords } from '../config/index.js';
import { getTravelTime } from '../lib/api.js';
import { estimateTravelTime } from '../lib/travelTime.js';

/**
 * Process-wide cache, keyed by origin *and* restaurant — mirrors
 * `usePlaceDetails`'s cache, and for the same reason: the deck mounts and
 * unmounts the same restaurant repeatedly as cards cycle.
 *
 * The origin is part of the key because it is no longer fixed. Keying on the
 * restaurant alone would show someone the previous neighbourhood's walking
 * time after they moved.
 *
 * @type {Map<string, {walkMinutes: number, driveMinutes: number, accurate: boolean}>}
 */
const cache = new Map();

/**
 * Real walk/drive time from the venue to a restaurant, via Routes API.
 * Starts from (and falls back to, if Routes isn't configured or the request
 * fails) the rough local estimate, so the card never shows nothing while
 * waiting on a network round trip.
 *
 * @param {import('../lib/types.js').Restaurant} restaurant
 * @param {object} [options]
 * @param {boolean} [options.enabled]  Skip the request when false.
 * @param {[number, number]} [options.origin]  Where to measure from.
 * @returns {{walkMinutes: number, driveMinutes: number, accurate: boolean}}
 */
export function useTravelTime(restaurant, { enabled = true, origin = null } = {}) {
  // Falls back to the configured venue when location isn't shared, which is
  // what the whole app did before location existed.
  const from = origin ?? venueCoords;
  const key = `${from.join(',')}|${restaurant.id}`;
  const cached = cache.get(key);
  const [times, setTimes] = useState(
    cached ?? { ...estimateTravelTime(from, restaurant.coords), accurate: false }
  );

  useEffect(() => {
    if (!enabled) return;

    if (cache.has(key)) {
      setTimes(cache.get(key));
      return;
    }

    let active = true;
    const fallback = { ...estimateTravelTime(from, restaurant.coords), accurate: false };

    getTravelTime(from, restaurant.coords)
      .then((result) => {
        const resolved =
          result?.walkMinutes != null || result?.driveMinutes != null
            ? {
                walkMinutes: result.walkMinutes ?? fallback.walkMinutes,
                driveMinutes: result.driveMinutes ?? fallback.driveMinutes,
                accurate: true,
              }
            : fallback;

        cache.set(key, resolved);
        if (active) setTimes(resolved);
      })
      .catch(() => {
        cache.set(key, fallback);
        if (active) setTimes(fallback);
      });

    return () => {
      active = false;
    };
  }, [enabled, key, restaurant.coords, from]);

  return times;
}
